import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Avatar,
  IconButton,
  Popover,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSupabase } from "../auth/useSupabase";
import leagueService from "../data/leagueService";
import { Tables } from "../../database.types";
import LeagueOverview from "./league-overview/LeagueOverview";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import { StandardButton } from "../components/StandardButton";
import { StandardTextInput } from "../components/StandardTextInput";
import { useNotification } from "../contexts/NotificationContext";
import { getErrorMessage } from "../utils/utils";
import profileService from "../data/profileService";

const AdminDashboard: React.FC = () => {
  const { user, supabase } = useSupabase();

  const theme = useTheme();

  const { showSnackbar } = useNotification();

  const [leagues, setLeagues] = useState<Tables<"leagues">[]>([]);
  const [selectedLeague, setSelectedLeague] =
    useLocalStorageState<Tables<"leagues"> | null>("selected-league", null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState<string | null>(null);
  const [profileName, setProfileName] = useState("");
  const [editName, setEditName] = useState("");
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const fetchLeagues = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const leaguesData = await leagueService.getAllUserLeagues(
        supabase,
        user.id,
      );
      setLeagues(leaguesData);

      // If there's a selected league in localStorage, verify it still exists
      if (selectedLeague) {
        const stillExists = leaguesData.find((l) => l.id === selectedLeague.id);
        if (!stillExists) {
          setSelectedLeague(null);
        } else if (stillExists.updated_at !== selectedLeague.updated_at) {
          // Only update if the data has actually changed (compare timestamps)
          setSelectedLeague(stillExists);
        }
      }
    } catch (err) {
      console.error("Error fetching leagues:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [user, supabase, selectedLeague, setSelectedLeague]);

  useEffect(() => {
    if (user) {
      fetchLeagues();
    }
  }, [user, fetchLeagues]);

  useEffect(() => {
    if (user) {
      profileService.getProfile(supabase, user.id).then((profile) => {
        if (profile) {
          setProfileName(profile.name);
        }
      });
    }
  }, [user, supabase]);

  const handleSaveProfileName = async () => {
    if (user) {
      const trimmed = editName.trim();
      if (!trimmed || trimmed === profileName) {
        setProfileAnchorEl(null);
        return;
      }
      const error = await profileService.updateProfileName(
        supabase,
        user.id,
        trimmed,
      );
      if (error) {
        showSnackbar(getErrorMessage(error), "error");
      } else {
        setProfileName(trimmed);
        showSnackbar("Name updated", "success");
      }
      setProfileAnchorEl(null);
    }
  };

  const handleCreateLeague = async () => {
    if (!user || !newLeagueName.trim()) return;

    try {
      setLoading(true);
      const newLeague = await leagueService.create(supabase, {
        name: newLeagueName.trim(),
        creator_id: user.id,
      });
      setLeagues([...leagues, newLeague]);
      setSelectedLeague(newLeague);
      setNewLeagueName("");
      setCreateDialogOpen(false);
    } catch (err) {
      console.error("Error creating league:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinLeague = async () => {
    if (!user || !joinCode.trim()) return;

    try {
      setJoinError(null);
      await leagueService.joinLeagueViaCode(supabase, user.id, joinCode.trim());
      setJoinCode("");
      await fetchLeagues();
    } catch (err) {
      showSnackbar(getErrorMessage(err), "error");
    }
  };

  // Handle loading state
  if (loading && leagues.length === 0) {
    return (
      <Box
        height="100vh"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={theme.palette.primary.dark}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} variant="filled">
          {error.message}
        </Alert>
      </Container>
    );
  }

  // Handle no user
  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" variant="filled">
          No user information available. Please sign in.
        </Alert>
      </Container>
    );
  }

  // If a league is selected, show the LeagueOverview
  if (selectedLeague) {
    return (
      <LeagueOverview
        league={selectedLeague}
        onBackToLeagueSelection={() => setSelectedLeague(null)}
        onLeagueUpdate={(updatedLeague) => {
          setSelectedLeague(updatedLeague);
          setLeagues(
            leagues.map((l) => (l.id === updatedLeague.id ? updatedLeague : l)),
          );
        }}
      />
    );
  }

  // Show league selection screen
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: theme.palette.primary.dark,
        p: 3,
        position: "relative",
      }}
    >
      {/* Profile Avatar */}
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <IconButton
          onClick={(e) => {
            setEditName(profileName);
            setProfileAnchorEl(e.currentTarget);
          }}
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.secondary.main,
              width: 40,
              height: 40,
              fontSize: 18,
              fontWeight: "bold",
            }}
          >
            {profileName ? profileName[0].toUpperCase() : "?"}
          </Avatar>
        </IconButton>
        <Popover
          open={Boolean(profileAnchorEl)}
          anchorEl={profileAnchorEl}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              label="Display Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveProfileName();
              }}
              autoFocus
            />
            <IconButton
              size="small"
              color="primary"
              onClick={handleSaveProfileName}
              disabled={!editName.trim()}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setProfileAnchorEl(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Popover>
      </Box>

      <Container maxWidth="md">
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          fontWeight="bold"
          textAlign="center"
          sx={{ mb: 4, color: "white" }}
        >
          Select a League
        </Typography>

        {leagues.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              No Leagues Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Get started by creating your first league.
            </Typography>
            <StandardButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="large"
            >
              Create League
            </StandardButton>
          </Paper>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}
            >
              {leagues.map((league) => (
                <Card
                  key={league.id}
                  sx={{
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardActionArea onClick={() => setSelectedLeague(league)}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        color="white"
                      >
                        {league.name}
                      </Typography>
                      <Typography variant="body2" color="white">
                        {league.creator_id === user.id
                          ? "League Owner"
                          : "League Participant"}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <StandardButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create New League
              </StandardButton>
            </Box>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mt: 4,
            justifyContent: "center",
          }}
        >
          <StandardTextInput
            label="Join Code"
            value={joinCode}
            onChange={(e) => {
              setJoinCode(e.target.value);
              setJoinError(null);
            }}
            onEnter={() => handleJoinLeague()}
            size="small"
            error={!!joinError}
            helperText={joinError}
          />
          <StandardButton
            variant="outlined"
            onClick={handleJoinLeague}
            disabled={!joinCode.trim()}
            sx={{
              height: 40,
              color: "white",
              borderColor: "white",
              ":disabled": {
                color: "gray",
                borderColor: "gray",
              },
            }}
          >
            Join
          </StandardButton>
        </Box>
      </Container>

      {/* Create League Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewLeagueName("");
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
          Create New League
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="League Name"
            fullWidth
            variant="outlined"
            value={newLeagueName}
            onChange={(e) => setNewLeagueName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newLeagueName.trim()) {
                handleCreateLeague();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <StandardButton
            onClick={() => {
              setCreateDialogOpen(false);
              setNewLeagueName("");
            }}
            color="inherit"
          >
            Cancel
          </StandardButton>
          <StandardButton
            onClick={handleCreateLeague}
            variant="contained"
            disabled={!newLeagueName.trim()}
          >
            Create
          </StandardButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
